import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa';
import Modal from './Modal';
import ApiService from '../ApiService';
import '../styles/SaveItineraryModal.css';
import { trackEvent } from '../analytics';

function SaveItineraryModal({ isOpen, onClose, user, waypoints, suggestedName, onSaveOffline, cacheProgress }) {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState(suggestedName || '');
  const [share, setShare] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(suggestedName || '');
      setShare(false);
      setError(false);
    }
  }, [isOpen, suggestedName]);

  const handleClose = () => {
    setError(false);
    onClose();
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setSaving(true);
    setError(false);
    try {
      await onSaveOffline(trimmed);

      if (share) {
        setSharing(true);
        const result = await ApiService.post('/itinerary/', {
          name: trimmed,
          language: i18n.language,
          travel_mode: 'WALK',
          waypoints,
        }, user);
        if (!result) {
          setError(true);
          return;
        }
      }

      trackEvent('route_saved_offline', { shared: share, place_count: waypoints.length });
      handleClose();
    } catch (e) {
      setError(true);
    } finally {
      setSaving(false);
      setSharing(false);
    }
  };

  const progressTotal = cacheProgress?.total ?? 0;
  const progressDone = Math.min(cacheProgress?.done ?? 0, progressTotal);
  const showingProgress = saving && !error;
  const headingKey = sharing ? 'save_itinerary_modal.step_sharing' : 'save_itinerary_modal.saving_label';

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="sim-content">
        <h2 className="sim-title">{t('save_itinerary_modal.title')}</h2>
        <div className="sim-body">
          {showingProgress ? (
            <div className="sim-progress-section">
              <p className="sim-progress-label">{t(headingKey)}</p>
              <FaSpinner className="sim-spinner" aria-hidden="true" />
              {progressTotal > 0 && !sharing && (
                <p className="sim-progress-count">
                  {t('save_itinerary_modal.progress_count', { done: progressDone, total: progressTotal })}
                </p>
              )}
            </div>
          ) : (
            <>
              <input
                className="sim-input"
                type="text"
                placeholder={t('save_itinerary_modal.name_placeholder')}
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                maxLength={100}
              />
              <label className="sim-share">
                <input
                  type="checkbox"
                  checked={share}
                  onChange={e => setShare(e.target.checked)}
                  disabled={saving}
                />
                <span>{t('save_itinerary_modal.share_label')}</span>
              </label>
              {error && <p className="sim-error">{t('save_itinerary_modal.error')}</p>}
            </>
          )}
          <div className="sim-actions">
            <button className="sim-btn-cancel" onClick={handleClose} disabled={saving}>
              {t('save_itinerary_modal.cancel')}
            </button>
            {!showingProgress && (
              <button
                className="sim-btn-save"
                onClick={handleSave}
                disabled={saving || !name.trim()}
              >
                {t('save_itinerary_modal.save')}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default SaveItineraryModal;
